<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <!-- Use flexbox with no wrapping so title and buttons always stay on one line -->
        <v-card-title class="ww-page-header">
          <div class="ww-page-heading">
            <div class="ww-page-title">{{ adminMode ? 'Form Designer — System Forms' : 'Form Design' }}</div>
            <div class="ww-page-subtitle">
              {{ adminMode
                ? 'Create and manage the system-wide forms shared with every customer'
                : 'View and manage all of your forms' }}
            </div>
          </div>
          <div class="d-flex ga-2">
            <!-- Defaults are the admin's own rows, so the picker is customer-side only. -->
            <v-btn v-if="!adminMode" color="primary" variant="outlined" small @click="openPresetPicker">
              <v-icon left size="18">mdi-library-plus</v-icon>
              Add from Defaults
            </v-btn>
            <v-btn color="success" dark small @click="onAddForm">
              <v-icon left size="18">mdi-plus</v-icon>
              {{ adminMode ? 'Create System Form' : 'Create New Form' }}
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row style="margin-top: 0px">
      <v-col cols="12">
        <v-card elevation="3">
          <!-- Card header with title, search, and add button -->
          <v-toolbar flat color="white" dense class="px-4 align-center">
            <v-toolbar-title class="pa-0 ma-0 d-flex align-center">
              <v-text-field
                v-model="search"
                placeholder="Search forms..."
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                density="compact"
                variant="outlined"
                class="mx-2 pa-0 search-field"
                style="max-width: 300px"
              />
            </v-toolbar-title>
            <v-spacer />
          </v-toolbar>

          <!-- Data table -->
          <v-data-table
            :headers="headers"
            :items="filteredForms"
            :loading="loading"
            class="elevation-1 forms-table"
            item-key="formId"
            density="comfortable"
            loading-text="Loading forms…"
            no-data-text="No forms found."
            striped
            :items-per-page="10"
            :items-per-page-options="[5, 10, 25, 50, 100]"
            :sort-by="[{ key: 'lastUpdated', order: 'desc' }]"
            @click:row="onRowClick"
          >
            <template #item.formId="{ item }">
              <span class="text-body-2 text-medium-emphasis">{{ item.formId }}</span>
            </template>
            <template #item.name="{ item }">
              <span class="text-body-2 font-weight-medium text-primary">{{ item.name }}</span>
            </template>
            <template #item.isGlobal="{ item }">
              <v-chip
                :color="item.isGlobal ? 'indigo' : 'grey-lighten-1'"
                size="small"
                variant="flat"
                density="compact"
              >
                <v-icon start size="14">
                  {{ item.isGlobal ? 'mdi-earth' : 'mdi-account' }}
                </v-icon>
                {{ item.isGlobal ? 'System' : 'Custom' }}
              </v-chip>
            </template>
            <template #item.lastUpdated="{ item }">
              <span class="text-body-2">{{ formatDate(item.lastUpdated) }}</span>
            </template>
            <template #item.isPublic="{ item }">
              <v-chip
                :color="item.isPublic ? 'success' : 'grey-lighten-1'"
                size="small"
                variant="flat"
                density="compact"
              >
                <v-icon start size="14">
                  {{ item.isPublic ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
                {{ item.isPublic ? 'Public' : 'Private' }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex align-center justify-center ga-1" @click.stop>
                <v-tooltip text="View Form">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="primary"
                      @click.stop="viewForm(item.formId)"
                    >
                      <v-icon size="18">mdi-eye</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip text="Duplicate Form">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="primary"
                      @click.stop="onDuplicateForm(item.formId)"
                    >
                      <v-icon size="18">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip v-if="canEditRow(item)" text="Edit Form">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="primary"
                      @click.stop="editForm(item.formId)"
                    >
                      <v-icon size="18">mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip v-if="canEditRow(item)" text="Delete Form">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      @click.stop="onDeleteForm(item.formId)"
                    >
                      <v-icon size="18">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip v-else text="System form — read-only">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="18" color="grey">mdi-lock</v-icon>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add from Defaults: pick a preset, get your own independent copy of it. -->
    <v-dialog v-model="presetDialog" max-width="700">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start color="primary">mdi-library-plus</v-icon>
          Add from Defaults
        </v-card-title>
        <v-card-subtitle class="pb-2">
          Choose a standard form to add to your own forms. You get your own copy — edit it
          however you like; the original is not affected.
        </v-card-subtitle>
        <v-divider />

        <v-card-text>
          <v-text-field
            v-model="presetSearch"
            placeholder="Search defaults..."
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            density="compact"
            variant="outlined"
            class="mb-3"
          />

          <div v-if="presetsLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <v-list v-else-if="filteredPresets.length" density="comfortable" class="preset-list">
            <v-list-item
              v-for="preset in filteredPresets"
              :key="preset.formId"
              :title="preset.name"
              :subtitle="`Default form #${preset.formId}`"
            >
              <template #prepend>
                <v-icon color="indigo">mdi-file-document-outline</v-icon>
              </template>
              <template #append>
                <v-btn
                  color="success"
                  size="small"
                  variant="tonal"
                  :loading="copyingPresetId === preset.formId"
                  :disabled="copyingPresetId !== null"
                  @click="onCopyPreset(preset)"
                >
                  <v-icon start size="16">mdi-plus</v-icon>
                  Add
                </v-btn>
              </template>
            </v-list-item>
          </v-list>

          <div v-else class="text-center py-6 text-medium-emphasis">
            {{ presetSearch ? 'No defaults match your search.' : 'No default forms are available.' }}
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="presetDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  fetchForm,
  fetchFormsAll,
  createForm,
  deleteForm,
  fetchFormPresets,
  copyFormPreset,
} from "@/api/formsTemplates";
import { useDialogStore } from "@/stores/dialog";

export default {
  name: "FormList",
  props: {
    // When true (the System Admin route) the grid manages the DEFAULTS library
    // (Customer 0/NULL) — the admin owns those rows and edits them here. For normal
    // customers the grid shows only their own forms; defaults never appear, they are
    // taken via "Add from Defaults", which copies one into a fresh row of their own.
    adminMode: { type: Boolean, default: false },
  },
  data() {
    return {
      forms: [],
      loading: false,
      search: "",
      presetDialog: false,
      presets: [],
      presetsLoading: false,
      presetSearch: "",
      copyingPresetId: null,
    };
  },
  computed: {
    // Customers only ever see their own forms now, so the Scope column would read
    // "Custom" on every row — it is only meaningful in the admin defaults view.
    headers() {
      const cols = [
        { title: "ID", key: "formId", sortable: true, width: "80px" },
        { title: "Name", key: "name", sortable: true },
        { title: "Last Updated", key: "lastUpdated", sortable: true, width: "180px" },
        { title: "Public", key: "isPublic", sortable: true, width: "120px", align: "center" },
        { title: "Actions", key: "actions", sortable: false, width: "140px", align: "center" },
      ];
      if (this.adminMode) {
        cols.splice(2, 0, {
          title: "Scope", key: "isGlobal", sortable: true, width: "120px", align: "center",
        });
      }
      return cols;
    },
    filteredForms() {
      if (!this.search) return this.forms;
      const term = this.search.toLowerCase();
      return this.forms.filter((f) =>
        Object.values(f).some((val) => String(val).toLowerCase().includes(term))
      );
    },
    filteredPresets() {
      if (!this.presetSearch) return this.presets;
      const term = this.presetSearch.toLowerCase();
      return this.presets.filter((p) => String(p.name || "").toLowerCase().includes(term));
    },
  },
  methods: {
    // A customer may manage their OWN forms; system/global rows are read-only for them.
    // Admin mode makes everything editable.
    canEditRow(item) {
      return this.adminMode || !item?.isGlobal;
    },
    async openPresetPicker() {
      this.presetDialog = true;
      this.presetSearch = "";
      this.presetsLoading = true;
      try {
        this.presets = await fetchFormPresets();
      } catch (err) {
        this.presets = [];
        this.$toast?.error("Could not load the default forms. Please try again.");
      } finally {
        this.presetsLoading = false;
      }
    },
    // Copies the default into this customer as a brand new form. From then on it is
    // entirely theirs — no link back to the default it came from.
    async onCopyPreset(preset) {
      this.copyingPresetId = preset.formId;
      try {
        const created = await copyFormPreset(preset.formId);
        this.$toast?.success(`"${created?.name || preset.name}" added to your forms`);
        this.presetDialog = false;
        await this.fetchForms();
      } catch (err) {
        const errorMsg =
          err.response?.data?.error || err.message || "Failed to add the form. Please try again.";
        this.$toast?.error(errorMsg);
      } finally {
        this.copyingPresetId = null;
      }
    },
    // Route names differ for the admin Form Designer so navigation stays inside /admin/forms.
    routeName(base) {
      const map = this.adminMode
        ? { create: "AdminFormCreate", edit: "AdminFormEdit", view: "AdminFormView", list: "AdminFormDesigner" }
        : { create: "FormCreate", edit: "FormEdit", view: "FormView", list: "FormList" };
      return map[base];
    },
    async fetchForms() {
      this.loading = true;
      try {
        const resp = await fetchFormsAll();
        this.forms = resp.map((f) => ({ ...f, lastUpdated: f.lastUpdated || null }));
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    onAddForm() {
      this.$router.push({ name: this.routeName("create") });
    },
    onRowClick(event, { item }) {
      const formId = item.formId || item.FormId;
      if (formId) {
        this.viewForm(formId);
      }
    },
    viewForm(id) {
      this.$router.push({ name: this.routeName("view"), params: { id } });
    },
    editForm(id) {
      this.$router.push({ name: this.routeName("edit"), params: { id } });
    },
    async onDuplicateForm(id) {
      try {
        const existing = await fetchForm(id);
        
        // Build duplicate payload - exclude FormId, CustomerId, and LastUpdated
        const duplicate = {
          name: `Copy of ${existing.name || "Form"}`,
          formStructure: existing.formStructure || "[]",
          icon: existing.icon || null,
          iconColour: existing.iconColour || null,
          sortOrder: existing.sortOrder ?? 0,
          iconName: existing.iconName || null,
          isPublic: existing.isPublic ?? false,
          publicKey: existing.publicKey || null,
          isMasterSurvey: existing.isMasterSurvey ?? false,
          originalFormId: existing.formId?.toString() || null,
        };
        
        const response = await createForm(duplicate);
        
        // The API returns { FormId: "123" } or { formId: "123" } in the response body
        // But sometimes it might be nested or the structure is different
        // Try multiple ways to extract the ID
        let newId = null;
        
        // The response structure is: { formId: { formId: 123, name: '...', ... } }
        // So we need to handle both direct ID and nested structure
        
        // First try direct properties (case-sensitive first, then case-insensitive)
        if (response?.FormId != null && typeof response.FormId !== 'object') {
          newId = response.FormId;
        } else if (response?.formId != null) {
          // If formId is an object, extract the nested formId property
          if (typeof response.formId === 'object') {
            // Try to get formId.formId (the actual ID)
            newId = response.formId?.FormId || response.formId?.formId || response.formId?.id;
          } else {
            newId = response.formId;
          }
        } else if (response?.id != null && typeof response.id !== 'object') {
          newId = response.id;
        }
        
        // If we still have an object, try to extract from it recursively
        if (newId && typeof newId === 'object') {
          // Try common property names (case-sensitive first, then case-insensitive)
          newId = newId?.FormId || newId?.formId || newId?.id || newId?.value;
          
          // If still an object, recursively try to extract (but limit depth to avoid infinite loops)
          let depth = 0;
          const maxDepth = 3;
          while (newId && typeof newId === 'object' && depth < maxDepth) {
            depth++;
            const keys = Object.keys(newId);
            
            // Priority: look for FormId, formId, id, value (in that order)
            const priorityKeys = ['FormId', 'formId', 'id', 'value'];
            let found = false;
            for (const key of priorityKeys) {
              if (key in newId) {
                const val = newId[key];
                if (val != null && typeof val !== 'object') {
                  newId = val;
                  found = true;
                  break;
                }
              }
            }
            
            // If no priority key found, try to find any primitive value
            if (!found) {
              for (const key of keys) {
                const val = newId[key];
                if (val != null && typeof val !== 'object' && (typeof val === 'string' || typeof val === 'number')) {
                  newId = val;
                  found = true;
                  break;
                }
              }
            }
            
            // If still an object and we haven't found anything, try the first key
            if (!found && newId && typeof newId === 'object' && keys.length > 0) {
              const firstKey = keys[0];
              newId = newId[firstKey];
            } else if (!found) {
              break;
            }
          }
        }
        
        // Ensure it's a primitive (string or number)
        if (newId != null && typeof newId !== 'object') {
          newId = String(newId);
        } else {
          // Don't throw - just show error and refresh list
          this.$toast?.error("Form duplicated but could not get new ID. Please refresh the list.");
          await this.fetchForms();
          return;
        }
        
        if (!newId || newId === 'null' || newId === 'undefined') {
          this.$toast?.error("Form duplicated but could not get new ID. Please refresh the list.");
          await this.fetchForms();
          return;
        }
        
        this.$toast?.success("Form duplicated successfully");
        
        // Refresh the list to show the new form - STAY ON GRID, DON'T NAVIGATE
        await this.fetchForms();
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || "Failed to duplicate form. Please try again.";
        this.$toast?.error(errorMsg);
      }
    },
    async onDeleteForm(id) {
      const dialog = useDialogStore();
      const result = await dialog.confirm({
        title: "Delete Form",
        message: "Are you sure you want to delete this form?",
        variant: "warn",
        buttons: "ok-cancel",
      });
      if (result !== "ok") return;
      try {
        const response = await deleteForm(id);
        
        // Force refresh the forms list
        this.loading = true;
        await this.fetchForms();
        this.loading = false;
        
        this.$toast?.success("Form deleted successfully");
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || "Failed to delete form. Please try again.";
        this.$toast?.error(errorMsg);
        this.loading = false;
      }
    },
    // async onDeleteForm(id) {
    //   if (!confirm("Are you sure you want to delete this form?")) return;
    //   try {
    //     await deleteForm(id);
    //     this.fetchForms();
    //   } catch (err) {
    //     console.error("Delete failed:", err);
    //   }
    // },
    formatDate(dt) {
      if (!dt) return "—";
      try {
        const date = new Date(dt);
        if (isNaN(date.getTime())) return "—";
        // Format: "01 Jan 2025, 10:30 AM"
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) + ", " + date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (err) {
        return "—";
      }
    },
  },
  mounted() {
    this.fetchForms();
  },
};
</script>

<style scoped>
.grey--text {
  color: rgba(0, 0, 0, 0.6);
}

.search-field {
  max-width: 300px;
}

/* Improve table spacing */
:deep(.v-data-table) {
  border-radius: 4px;
}

:deep(.v-data-table__thead th) {
  font-weight: 600;
  background-color: #fafafa;
}

/* Make table rows clickable */
.forms-table :deep(.v-data-table__tr) {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.forms-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
.forms-table :deep(.v-data-table__tr:active) {
  background-color: rgba(0, 0, 0, 0.08) !important;
}
.text-primary {
  color: rgb(25, 118, 210);
  font-weight: 500;
}

/* Better action button spacing */
.ga-1 {
  gap: 4px;
}

/* Ensure consistent text styling */
.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6);
}
</style>
